import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: {
    campaign_id?: string;
    user_id?: string;
    campaign_name?: string;
    lead_response_id?: string;
    lead_name?: string;
    sender_name?: string;
    conversation_version?: number;
    message_count?: number;
    action_url?: string;
  };
  priority?: string;
  read: boolean;
  created_at: string;
  read_at?: string;
}

interface NotificationBellProps {
  userRole?: string | null;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ userRole }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [actualUserRole, setActualUserRole] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Load user role if not provided
  useEffect(() => {
    const loadUserRole = async () => {
      if (userRole !== undefined) {
        setActualUserRole(userRole);
        return;
      }
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: userData } = await supabase
            .from('users')
            .select('role')
            .eq('id', session.user.id)
            .single();
          
          setActualUserRole(userData?.role || 'user');
          console.log('NotificationBell: Loaded user role:', userData?.role);
        }
      } catch (error) {
        console.error('NotificationBell: Error loading user role:', error);
        setActualUserRole('user');
      }
    };
    
    loadUserRole();
  }, [userRole]);

  // Get relevant notification types based on user role
  const getRelevantNotificationTypes = useMemo(() => {
    // Use actualUserRole which is stable across renders
    const roleToCheck = actualUserRole || 'user';
    
    switch(roleToCheck) {
      case 'admin':
      case 'super_admin':
        return [
          'admin_campaign_submitted', 
          'campaign_running', 
          'system_announcement',
          'error_alert',
          'feature_update'
        ];
      case 'csr':
        return [
          'campaign_submitted',
          'campaign_paused',
          'campaign_cancelled',
          'campaign_resumed',
          'csv_upload_ready',
          'filter_request_submitted',
          'reply_requested',
          'sequence_continuation_requested',
          'csr_assigned',
          'lead_response_new',
          'report_ready_for_review',
          'system_announcement',
          'feature_update'
        ];
      default: // regular users
        return [
          'lead_response_new', 
          'lead_response_followup', 
          'reply_sent', 
          'near_message_limit',
          'conversation_closed',
          'campaign_approved', 
          'campaign_needs_editing',
          'campaign_running',
          'system_announcement',
          'feature_update'
        ];
    }
  }, [actualUserRole]);

  const loadNotifications = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      // Don't filter if we don't have a role yet - load all notifications
      if (!actualUserRole) {
        console.log('NotificationBell: Skipping load - role not ready');
        return;
      }

      // Load notifications with role-based filtering
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      // Filter by relevant types for this role
      query = query.in('type', getRelevantNotificationTypes);

      const { data, error } = await query;
      
      if (error) {
        console.error('Error loading notifications:', error);
        return;
      }

      console.log(`NotificationBell: Loaded ${data?.length || 0} notifications for role: ${actualUserRole}`);
      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.read).length || 0);
    } catch (error) {
      console.error('Error in loadNotifications:', error);
    } finally {
      setLoading(false);
    }
  }, [getRelevantNotificationTypes, actualUserRole]);

  useEffect(() => {
    // Only load notifications when we have a role
    if (actualUserRole) {
      loadNotifications();
    }
    
    // Set up real-time subscription for new notifications
    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !actualUserRole) return;

      const subscription = supabase
        .channel(`notifications:${user.id}`)
        .on('postgres_changes', 
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('New notification received:', payload);
            const newNotification = payload.new as Notification;
            
            // Only add if it's a relevant type for this user's role
            if (getRelevantNotificationTypes.includes(newNotification.type)) {
              setNotifications(prev => [newNotification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Play a sound if enabled (future feature)
              // playNotificationSound();
            }
          }
        )
        .on('postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            const updatedNotification = payload.new as Notification;
            setNotifications(prev => 
              prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
            );
            // Recalculate unread count
            loadNotifications();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    };

    const cleanup = setupSubscription();
    return () => {
      cleanup?.then(fn => fn?.());
    };
  }, [getRelevantNotificationTypes, loadNotifications, actualUserRole]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('id', notificationId);

      if (!error) {
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId 
              ? { ...n, read: true, read_at: new Date().toISOString() }
              : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
      
      if (unreadIds.length === 0) return;

      const { error } = await supabase
        .from('notifications')
        .update({ 
          read: true, 
          read_at: new Date().toISOString() 
        })
        .in('id', unreadIds);

      if (!error) {
        setNotifications(prev => 
          prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate to the appropriate page based on action_url or type
    if (notification.data.action_url) {
      navigate(notification.data.action_url);
      setIsOpen(false);
      return; // Exit early if action_url exists
    }
    
    // Fallback navigation based on type
    switch (notification.type) {
      case 'campaign_submitted':
        // CSRs need to go to approval page for new campaigns
        navigate('/campaign-approvals');
        break;
      case 'csv_upload_ready':
        // CSRs need to process CSV uploads
        navigate('/csr-campaigns');
        break;
      case 'filter_request_submitted':
        // CSRs need to build filter lists
        navigate('/csr-filter-requests');
        break;
      case 'report_ready_for_review':
        navigate('/csr-reports');
        break;
      case 'admin_campaign_submitted':
        navigate('/csr-campaigns');
        break;
      case 'csr_assigned':
        // When CSR is assigned to a campaign or company
        if (notification.data.campaign_id) {
          navigate('/csr-campaigns');
        } else {
          navigate('/csr-users');
        }
        break;
      case 'lead_response_new':
      case 'lead_response_followup':
      case 'reply_requested':
      case 'sequence_continuation_requested':
      case 'reply_sent':
      case 'near_message_limit':
      case 'conversation_closed':
        if (notification.data.lead_response_id) {
          navigate(`/leads-inbox?selected=${notification.data.lead_response_id}`);
        } else {
          navigate('/leads-inbox');
        }
        break;
      case 'campaign_paused':
      case 'campaign_cancelled':
      case 'campaign_resumed':
        navigate('/csr-campaigns');
        break;
      case 'campaign_approved':
      case 'campaign_needs_editing':
      case 'campaign_running':
        if (notification.data.campaign_id) {
          navigate(`/campaigns/${notification.data.campaign_id}`);
        } else {
          navigate('/campaigns');
        }
        break;
      case 'error_alert':
        // Admin error alerts - go to admin panel
        navigate('/admin');
        break;
      case 'system_announcement':
      case 'feature_update':
        // These typically don't require navigation
        // Could optionally navigate to a news/updates page if one exists
        break;
      default:
        // Stay on current page
        break;
    }
    
    setIsOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    const iconMap: { [key: string]: string } = {
      // Lead Response Events
      'lead_response_new': '💬',
      'lead_response_followup': '🔄',
      'reply_requested': '📨',
      'sequence_continuation_requested': '⏭️',
      'reply_sent': '✉️',
      'near_message_limit': '⚠️',
      'conversation_closed': '🔒',
      
      // Campaign Events
      'campaign_submitted': '📝',
      'csv_upload_ready': '📎',
      'filter_request_submitted': '🎯',
      'admin_campaign_submitted': '📋',
      'campaign_approved': '✅',
      'campaign_needs_editing': '✏️',
      'campaign_running': '🚀',
      'campaign_paused': '⏸️',
      'campaign_cancelled': '🚫',
      'campaign_resumed': '▶️',
      'campaign_completed': '🎉',
      
      // Report Events
      'report_ready_for_review': '📊',

      // System Events
      'csr_assigned': '👤',
      'system_announcement': '📢',
      'feature_update': '✨',
      'error_alert': '🚨',
    };
    
    return iconMap[type] || '🔔';
  };

  const getNotificationColor = (priority?: string, read?: boolean) => {
    if (!read) {
      switch (priority) {
        case 'high': return 'bg-red-50 border-red-200';
        case 'medium': return 'bg-yellow-50 border-yellow-200';
        default: return 'bg-blue-50 border-blue-200';
      }
    }
    return 'bg-white';
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const filteredNotifications = useMemo(() => {
    if (filter === 'unread') {
      return notifications.filter(n => !n.read);
    }
    return notifications;
  }, [notifications, filter]);

  // Don't show loading state if we're just waiting for the role
  if (loading && actualUserRole) {
    return (
      <div className="relative">
        <button className="relative p-2 text-gray-400">
          <svg className="w-6 h-6 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </button>
      </div>
    );
  }
  
  // If we don't have a role yet, show the bell without notifications
  if (!actualUserRole) {
    return (
      <div className="relative">
        <button className="relative p-2 text-gray-600 hover:text-gray-800 transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-800 transition-colors"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Mark all read
                </button>
              )}
            </div>
            
            {/* Filter tabs */}
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 text-sm rounded ${
                  filter === 'all' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1 text-sm rounded ${
                  filter === 'unread' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>
          </div>

          {/* Notifications list */}
          <div className="flex-1 overflow-y-auto">
            {filteredNotifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-sm">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    getNotificationColor(notification.priority, notification.read)
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className={`text-sm font-medium ${
                            !notification.read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </h4>
                          <p className={`text-sm mt-1 ${
                            !notification.read ? 'text-gray-700' : 'text-gray-500'
                          }`}>
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2 mt-1"></span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate('/profile?tab=notifications');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View all notifications & settings
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;